import { useRef, useState, type ReactNode } from 'react';
import { ImageUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * Görsel yükleme: önizleme, yükle/değiştir ve (isteğe bağlıysa) kaldır. Dosya sunucuda yeniden kodlanır ve
 * meta verisi atılır; değer yüklenen dosyanın kimliğidir.
 */
export function ImageUpload({
  value,
  onChange,
  label,
  help,
  removable,
  wide,
}: {
  value: string | null;
  onChange: (fileId: string | null) => void;
  /** Düğmenin ve dosya seçicinin erişilebilir adı (ör. "Masaüstü görseli yükle"). */
  label?: string;
  help?: ReactNode;
  removable?: boolean;
  wide?: boolean;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const uploadLabel = label ?? (value ? t('admin.pages.replaceImage') : t('admin.pages.uploadImage'));
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        className={`flex items-center justify-center overflow-hidden rounded-sm border border-line bg-surface-2 ${wide ? 'h-20 w-36' : 'h-16 w-24'}`}
      >
        {value ? (
          <img src={`/v1/files/${value}/content`} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-ink-3">{t('admin.pages.noImage')}</span>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
        tabIndex={-1}
        aria-label={uploadLabel}
        className="sr-only"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.upload<{ id: string }>('/v1/admin/files?purpose=general', fd);
            onChange(r.id);
          } catch (err) {
            toast.error(err instanceof ApiError ? err.title : String(err));
          } finally {
            setBusy(false);
            if (input.current) input.current.value = '';
          }
        }}
      />
      <Button size="sm" loading={busy} onClick={() => input.current?.click()}>
        <ImageUp className="h-4 w-4" aria-hidden />
        {uploadLabel}
      </Button>
      {help}
      {removable && value && (
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          {t('common.remove')}
        </Button>
      )}
    </div>
  );
}
